import React from 'react';
import * as IoC from 'typescript-ioc';
import {
    AudioBridgeProvider,
    ConnectionType,
    IAudioBridgeResponse,
    IChildConnectionDTO,
    IConnectionAttendeeDTO,
    IConnectionDetailsDTO,
    IConnectionGroupDTO,
    KnovioLabel
} from '../../api/connections.dao';
import { IScheduledZoomMeetingDetailsDTO, IWebinarRequestParams, IZoomDAO, IZoomMeetingListItemDTO, IZoomMeetingRequestParams, UniqueDialInData } from '../../api/zoom.dao';
import { ConnectionVersion, EnablementStatus, NO, YES } from '../../constants/common.constants';
import { ConnectionEntity } from '../../modules/connections/abstract-connection.entity';
import { ConnectionFields } from '../../modules/connections/config';
import { isTrackingFieldEnabled } from '../../tracking-service/tracking-values';
import { FormattedMessage } from 'react-intl';
import { ZoomMeetingWithDateFields } from '../../components/_zoom/_zoom-meetings/_scheduled-zoom-meetings/_scheduled-zoom-meeting-details/scheduled-zoom-meeting-details.viewmodel';
import { ApprovalType } from '../../components/_events/_manage-event/shared/connections-list/_create-zoom-webinar-modal/create-zoom-webinar-form-data.viewmodel';
import { IKnovioConnection, IOtherConnection, IZoomConnection, TConnection } from '../../api/blueprints/connection.template.types';
import {
    AudioBridgeConnectionField
} from '../../components/_events/_manage-event/shared/connections-list/_connection-form/connection-form-body/audio-bridge-connection-form/audio-bridge-connection.form-config';
import { Permission } from '../../api/roles.dao';
import { hasPermission } from './has-permissions';
import { AudioBridgeProviderLabel } from './common.utils';
import { IGlobalStorage } from '../global-storage';
import { KnovioLinkViewModel } from '../../components/_events/_manage-event/shared/connections-list/_connection-form/knovio-link.viewmodel';
import { getOneclickStartLivestreamLinkById } from './knovio.utils';
import { IAudioBridgeDAO, IAudioBridgeRequestParams } from '../../api/audio-bridge.dao';
import TrackingServiceProvider from '../../tracking-service/tracking-service-provider';

export const getLabelKeysByType = (type: ConnectionType): string[] => (
    {
        [ConnectionType.Other]: [ ConnectionFields.NAME, ConnectionFields.CONNECTION_ID ],
        [ConnectionType.Knovio]: [ ConnectionFields.KNOVIO_NAME, ConnectionFields.EMBED_CODE_ID ],
        [ConnectionType.InPerson]: [ ConnectionFields.NAME ],
        [ConnectionType.Showcase]: [ ConnectionFields.SHOWCASE_PAGE_PATH ],
        [ConnectionType.Veracast]: [ ConnectionFields.VERACAST_EVENT_ID ],
        [ConnectionType.AudioBridge]: [ AudioBridgeConnectionField.LABEL, AudioBridgeConnectionField.CONFERENCE_ID ]
    }
)[type] || [ ConnectionFields.LABEL, ConnectionFields.CONNECTION_ID ];

export const getItemLabelValue = (item: IConnectionDetailsDTO): string => getLabelKeysByType(item.type).map(key => item[key]).find(Boolean) || '';

export const showNewUniqueConnection = (connections: IConnectionDetailsDTO[]): boolean =>
    hasPermission(Permission.CREATE_AUTOSYNC_CONNECTION)
    && connections.some(item => item.provider === AudioBridgeProviderLabel[AudioBridgeProvider.TURBOBRIDGE]);

export const isConnectionGroupType = (connectionItem: IConnectionDetailsDTO | IConnectionGroupDTO): boolean => {
    return connectionItem.connectionVersion === ConnectionVersion.V2_MULTIPLE || Reflect.has(connectionItem, 'joinMethods');
};

const getConnectionGroupLabelText = (group: IConnectionGroupDTO): string => group.label;

export const getConnectionItemLabelText = (item: IConnectionDetailsDTO | IConnectionGroupDTO): string => {
    if (isConnectionGroupType(item)) return getConnectionGroupLabelText(item as IConnectionGroupDTO);

    // type casting
    const connection = item as IConnectionDetailsDTO;
    const uniqueReferenceLabel = connection.meetingUniqueReference ? `${connection.meetingUniqueReference}: ` : '';
    const otherLabel = uniqueReferenceLabel + getLabelKeysByType(connection.type).map(key => connection[key]).filter(Boolean).join(': ');
    const itemLabel = getItemLabelValue(connection) ? getItemLabelValue(connection) : '';
    const connectionTypeLabel = connection.type === ConnectionType.Knovio ? KnovioLabel.OEStream : connection.type;
    const label = connection.type === ConnectionType.Other
        ? otherLabel
        : connection.connectionVersion === ConnectionVersion.V2 && connection.type === ConnectionType.PanelistLink
        ? itemLabel
        : `${uniqueReferenceLabel}${connectionTypeLabel}: ${itemLabel}`;
    return label;
};

export const getConnectionTemplateItemLabelText = (connection: TConnection): string => {
    const meetingUniqueReferencePrefix = connection.meetingUniqueReference ? `${connection.meetingUniqueReference}: ` : '';
    const connectionLabel = connection.label || (connection as IOtherConnection).name || (connection as IKnovioConnection).title || '';
    const connectionTypeLabel = connection.type === ConnectionType.Knovio ? KnovioLabel.OEStream : connection.type;
    return `${meetingUniqueReferencePrefix}${connectionTypeLabel} ${connectionLabel ? ': ' + connectionLabel : ''}`;
};

export const getComposedConnectionValues = (prevConnections: IConnectionDetailsDTO[], updatedConnection: IConnectionDetailsDTO): IConnectionDetailsDTO => {
    const prevConnection = prevConnections.find(el => Number(el.meetingConnectionId) === Number(updatedConnection.meetingConnectionId));
    if (!prevConnection) return updatedConnection;

    return {
        ...prevConnection,
        connectionId: updatedConnection.connectionId,
        type: updatedConnection.type,
        knovioName: updatedConnection.knovioName,
        embedCodeId: updatedConnection.embedCodeId,
        name: updatedConnection.name,
        hostLink: updatedConnection.hostLink,
        connectionLink: updatedConnection.connectionLink,
        connectionHostKey: updatedConnection.connectionHostKey,
        showcaseName: updatedConnection.showcaseName,
        showcasePagePath: updatedConnection.showcasePagePath,
        sip: updatedConnection.sip,
        meetingConnectionId: updatedConnection.meetingConnectionId,
        connectionStatus: updatedConnection.connectionStatus,
    };
};

export const getFilteredUniqueJoiningLink = (
    attendeeEntity: ConnectionEntity<IConnectionAttendeeDTO>,
    meetingConnections: IConnectionDetailsDTO[],
    assignedConnectionId: number,
    isRoleRegistrant: boolean
): IConnectionDetailsDTO[] => {
    const { email, meetingConnectionType } = attendeeEntity.getProps();
    return meetingConnections.filter((meeting: IConnectionDetailsDTO) => {
        if (
            meetingConnectionType === ConnectionType.UniqueJoiningLink &&
            assignedConnectionId === meeting.meetingConnectionId
        ) return true;
        if (email === meeting.attendeeEmail) return true;
        if (
            isRoleRegistrant &&
            meeting.type !== ConnectionType.UniqueJoiningLink &&
            meeting.meetingConnectionId === assignedConnectionId
        ) return true;
        if (!isRoleRegistrant && meeting.type !== ConnectionType.UniqueJoiningLink) return true;
        return false;
    });
};

type InitialZoomWebinarToggleValues = Record<string, string>;
type InitialKnovioPodiumTrackFields = Record<string, string>;

const zoomWebinarTogglesValues: InitialZoomWebinarToggleValues = {
    [ConnectionFields.AUTO_REGISTRATION_ENABLED]: 'Auto-Reg',
    [ConnectionFields.SYNC_MEETING_ATTENDEE_WITH_ZOOM_ENABLED]: 'Auto Sync',
    [ConnectionFields.REGISTRATION_ENABLED]: 'Registration Enabled',
    [ConnectionFields.AUTOMATICALLY_APPROVAL]: 'Automatic Approval',
    [ConnectionFields.REGISTRATION_CONFIRMATION_EMAIL]: 'Zoom Emails Enabled'
};

const knovioPodiumTrackFields: InitialKnovioPodiumTrackFields = {
    [ConnectionFields.PODIUM_LINK]: 'Podium Link',
    [ConnectionFields.PODIUM_EMBED_CODE]: 'Podium Embed Code',
    [ConnectionFields.PODIUM_ACCESS_CODE]: 'Podium Access Code'
};

export const additionalTrakingForConnection = (response: IConnectionDetailsDTO, trackingFields: Record<string, string | boolean>) => {
    Object.keys(zoomWebinarTogglesValues)
        .forEach(toggle => {
            const toggleValue = toggle === ConnectionFields.AUTOMATICALLY_APPROVAL
                ? (response[toggle] !== 0 ? EnablementStatus.DISABLED_UPPERCASE : EnablementStatus.ENABLED_UPPERCASE)
                : isTrackingFieldEnabled(response[toggle])
            ;
            trackingFields[zoomWebinarTogglesValues[toggle]] = toggleValue;
        });
};

export const attachKnovioPodiumTrackFields = (response: IConnectionDetailsDTO, trackingFields: Record<string, string | boolean>) => {
    Object.keys(knovioPodiumTrackFields)
        .forEach(podiumKey => {
            trackingFields[knovioPodiumTrackFields[podiumKey]] = !response[podiumKey] ? NO : YES;
        });
};

type meetingWebinarRequestType = IWebinarRequestParams | IZoomMeetingRequestParams;
export const meetingWebinarRequestDetails = (): Record<
    string, (params: meetingWebinarRequestType) => Promise<IScheduledZoomMeetingDetailsDTO>
> => {
    const zoomDAO = IoC.Container.get<IZoomDAO>(IZoomDAO);
    return {
        [ConnectionType.Webinar]: async (params: IWebinarRequestParams & { meetingId: number }) => {
            const webinarId = String(params.meetingId);
            const webinarParams = { accountId: params.accountId, eventId: params.eventId, webinarId: webinarId };
            return zoomDAO.getZoomWebinarDetails(webinarParams);
        },
        [ConnectionType.Zoom]: async (params: IZoomMeetingRequestParams) => {
            return zoomDAO.getSingleZoomMeetingDetails({ meetingId: params.meetingId });
        },
    };
};

export const meetingAudioBridgeRequestDetails = async (params: IAudioBridgeRequestParams): Promise<IAudioBridgeResponse> => {
    const audioBridgeDAO = IoC.Container.get<IAudioBridgeDAO>(IAudioBridgeDAO);
    return audioBridgeDAO.getAudioBridgeDetails(params);
};

export const meetingWebinarErrorMessage = {
    [ConnectionType.Webinar]: <FormattedMessage
        id='oe.zoomWebinar.webinarNotFound'
        description='Info text'
        defaultMessage='Zoom webinar not found'
    />,
    [ConnectionType.Zoom]: <FormattedMessage
        id='oe.zoomMeeting.meetingNotFound'
        description='Info text'
        defaultMessage='Zoom Meeting Not Found'
    />,
};

export const meetingWebinarSuccessMessage = {
    [ConnectionType.Webinar]: <FormattedMessage
        id='oe.zoomWebinar.webinarfound'
        description='Info text'
        defaultMessage='Webinar found'
    />,
    [ConnectionType.Zoom]: <FormattedMessage
        id='oe.zoomMeeting.meetingFound'
        description='Info text'
        defaultMessage='Zoom Meeting Found'
    />,
};

type ZoomMeeting = ZoomMeetingWithDateFields | IZoomMeetingListItemDTO;
export const isZoomMeetingReccuring = (zoomMeetingDetails: ZoomMeeting): boolean => {
    if (!zoomMeetingDetails) return false;
    return !zoomMeetingDetails.startTime && !zoomMeetingDetails.endTime;
};

export const connectionsToBluepringTemplateDTO = (connections: TConnection[]): TConnection[] => {
    return connections.map(c => {
        switch (c.type) {
            case ConnectionType.Zoom:
            case ConnectionType.Webinar:
                return convertZoomWebinarConnection(c as IZoomConnection);
            case ConnectionType.Knovio:
                return c;
            default:
                return convertOtherConnections(c as IOtherConnection);
        }
    });
};
const convertZoomWebinarConnection = (connection: IZoomConnection): IZoomConnection => {
    const approvalType = !connection.registrationEnabled
        ? ApprovalType.NOT_REQUIRED
        : connection.approvalType
    ;
    return {
        ...connection,
        approvalType: approvalType,
        registrantsConfirmationEmail: connection.registrationEnabled && connection.registrantsConfirmationEmail,
    };
};
const convertOtherConnections = (connection: IOtherConnection): IOtherConnection => ({
    ...connection,
    connectionId: connection.customConnectionId ? connection.customConnectionId : connection.connectionId
});

export const meetingWebinarText = {
    [ConnectionType.Webinar]: <FormattedMessage
        id='oe.zoomWebinar.webinarConnection'
        description='Info text'
        defaultMessage='Webinar'
    />,
    [ConnectionType.Zoom]: <FormattedMessage
        id='oe.zoomMeeting.zoomConnection'
        description='Info text'
        defaultMessage='Meeting'
    />,
};

export const composeConnectionDetailsCopyAllMessage = (connection: IConnectionDetailsDTO, audioProviderLabel?: string): string => {
    let messageList = [];
    switch (connection.type) {
        case ConnectionType.AudioBridge:
            messageList = [
                connection.label && 'Connection Name: Audio Bridge: ' + connection.label,
                connection.provider && 'Provider: ' + connection.provider,
                connection.audioBridgeName && 'Audio Bridge Name: ' + connection.audioBridgeName,
                connection.audioBridgeID && 'Audio Bridge ID: ' + connection.audioBridgeID,
                connection.audioProviderId && 'Telephone Number Set: ' + (audioProviderLabel || ''),
                connection.participantTelephoneNumbers && connection.participantTelephoneNumbers.length && 'Participant Telephone Numbers: ' + connection.participantTelephoneNumbers.split('\n')[0],
                connection.conferenceId && 'Conference ID: ' + connection.conferenceId,
                connection.participantId && 'Participant ID: ' + connection.participantId,
                connection.connectionPassword && 'Passcode: ' + connection.connectionPassword,
                connection.operatorTelephoneNumber && 'Operator Telephone Number: ' + connection.operatorTelephoneNumber,
                connection.operatorPin && 'Operator PIN: ' + connection.operatorPin
            ];
            break;
        case ConnectionType.Bluejeans:
        case ConnectionType.Teams:
        case ConnectionType.Other:
        case ConnectionType.Webex:
        case ConnectionType.Webinar:
        case ConnectionType.Zoom:
            messageList = [
                connection.connectionLink && 'Link: ' + connection.connectionLink,
                connection.alternativeLink && 'Alternative Link: ' + connection.alternativeLink,
                connection.connectionId && 'Meeting ID: ' + connection.connectionId,
                connection.connectionPassword && 'Passcode: ' + connection.connectionPassword,
                connection.sip && 'SIP: ' + connection.sip,
                connection.registrationUrl && 'Registration Link: ' + connection.registrationUrl
            ];
            break;
        case ConnectionType.Knovio:
            messageList = [
                connection.embedCodeId && `${KnovioLabel.Project} Embed Code ID: ` + connection.embedCodeId,
                connection.embedCodeId && `${KnovioLabel.Project} Link: ` + KnovioLinkViewModel.getKnovioLinkById(connection.embedCodeId),
                connection.knovioName && `${KnovioLabel.Project} Name: ` + connection.knovioName,
                connection.embedCodeId && 'Oneclick Start Livestream Link: ' + getOneclickStartLivestreamLinkById(connection.embedCodeId),
                connection.podiumLink && 'Podium 1.0 Link: ' + connection.podiumLink,
                connection.podiumAccessCode && 'Podium 1.0 Access Code: ' + connection.podiumAccessCode,
                connection.podiumEmbedCode && 'One-Click Podium 1.0 Link: ' + connection.podiumEmbedCode,
            ];
            break;
        case ConnectionType.PanelistLink:
            messageList = [
                connection.connectionLink && 'Connection Link: ' + connection.connectionLink,
                connection.participantId && 'Participant ID: ' + connection.participantId,
                connection.attendeeId && 'Attendee ID: ' + connection.attendeeId,
                connection.attendeeEmail && 'Attendee Email: ' + connection.attendeeEmail,
                connection.connectionId && 'Webinar ID: ' + connection.connectionId,
            ];
            break;
        default:
            break;
    }

    return messageList.filter(Boolean).join('\n');
};

export const composeChildConnectionDetailsCopyAllMessage = (childConnection: IChildConnectionDTO): string => {
    let messageList = [];
    switch (childConnection.type) {
        case ConnectionType.UniqueJoiningLink:
            messageList = [
                childConnection.attendeeEmail && 'AttendeeEmail: ' + childConnection.attendeeEmail,
                childConnection.attendeeId && 'Attendee Id: ' + childConnection.attendeeId,
                childConnection.connectionLink && 'Connection Link: ' + childConnection.connectionLink,
                childConnection.parentConnectionId && 'Connection ID: ' + childConnection.parentConnectionId,
            ];
            break;
        case ConnectionType.UniqueDialIn:
            messageList = [
                childConnection.attendeeId && 'OEC Attendee ID: ' + childConnection.attendeeId,
                childConnection.attendeeEmail && 'AttendeeEmail: ' + childConnection.attendeeEmail,
                childConnection.uniqueDialInCode && 'Participant ID: ' + childConnection.uniqueDialInCode,
                childConnection.conferenceId && 'Conference ID: ' + childConnection.conferenceId,
                childConnection.userId && 'User ID: ' + childConnection.userId,
            ];
            break;
        case ConnectionType.PanelistLink:
            messageList = [
                childConnection.connectionLink && 'Connection Link: ' + childConnection.connectionLink,
                childConnection.participantId && 'Participant ID: ' + childConnection.participantId,
                childConnection.attendeeId && 'Attendee ID: ' + childConnection.attendeeId,
                childConnection.attendeeEmail && 'Attendee Email: ' + childConnection.attendeeEmail,
                childConnection.connectionId && 'Webinar ID: ' + childConnection.connectionId
            ];
        default:
            break;
    }

    return messageList.filter(Boolean).join('\n');
};

export const renderEmails = (emails: string[]): JSX.Element[] => {
    return emails.map((email, index, list) => {
        const listLenght = list.length;
        const isLastElement = listLenght - 1 === index;

        return (
            <span key={email + index}>
                { email }
                { !isLastElement && ', ' }
            </span>
        );
    });
};

export const createUniqueDailInConnection = (
    audioBridgeId: string,
    connectionId: number,
    attendeeInfo: Partial<IConnectionAttendeeDTO>
): Promise<UniqueDialInData> => {
    const globalStorage = IoC.Container.get(IGlobalStorage);
    const zoomDAO = IoC.Container.get<IZoomDAO>(IZoomDAO);
    const uniqueDialInParams = {
        accountId: globalStorage?.getAccount()?.accountId,
        eventId: globalStorage?.getEvent()?.eventId,
        audioBridgeId: audioBridgeId,
        meetingConnectionId: connectionId,
        attendees: [{ attendeeId: attendeeInfo.attendeeId, firstName: attendeeInfo.firstName, lastName: attendeeInfo.lastName }]

    };
    return zoomDAO.createUniqueDialInConnection(uniqueDialInParams);
};

export const trackMixPanelPanelistDeletion = (eventName: string, meetingId: string | number): void => {
    const globalStorage = IoC.Container.get(IGlobalStorage);
    const trackingService = IoC.Container.get(TrackingServiceProvider);
    trackingService.get().track(eventName,{
        'Account ID': globalStorage.getAccount().accountId,
        'Account Name': globalStorage.getAccount().name,
        'Event ID': globalStorage.getEvent().eventId,
        'Event Name': globalStorage.getEvent().name,
        'Meeting ID': meetingId,
    });
};

export const getSortedConnections = (meetingConnections: IConnectionDetailsDTO[]): IConnectionDetailsDTO[] => {
    return meetingConnections.reduce((acc, connection) => {
        if (connection.type === ConnectionType.Webinar && connection.childConnections?.length > 0) {
            connection.childConnections.forEach(childConnection => {
                childConnection.connectionVersion = ConnectionVersion.V2;
                childConnection.parentMeetingConnectionId = connection.meetingConnectionId;
                childConnection.connectionStatus = connection.connectionStatus;
            });
            acc.push(connection);
        } else if (connection.type !== ConnectionType.UniqueJoiningLink) {
            acc.push(connection);
        }
        return acc;
    }, []);
};

export const isV2PanelistLink = (connection: IConnectionDetailsDTO) => {
    return connection.connectionVersion === ConnectionVersion.V2 && connection.type === ConnectionType.PanelistLink;
};